import { useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  aiPauseSet,
  aiPauseState,
  bell,
  killActivate,
  killDeactivate,
  killSwitches,
  ledgerVerify,
  metricsMonitoring,
  oversightGuards,
  oversightHeld,
  oversightHeldDecide,
  oversightHuman,
  oversightReviewers,
  oversightThread,
  oversightTraces,
  policyPosture,
  pulseIncidentAck,
  pulseIncidents,
  pulseV3Facilities,
  type LedgerRangeVerification,
  type OversightEvent,
  type OversightHeldDecision,
  type OversightHeldItem,
  type OversightTrace,
  type PulseIncident,
} from '@aithora/shared';
import { useConsoleRole, useSession } from '../../session/SessionContext';
import { useNavBadges } from '../../shell/Shell';
import { plainProblemTitle } from '../pulse/plainTitle';
import { ConfirmDialog, withConfirm } from '../../ui/ConfirmDialog';
import { sx } from '../../ui/designStyles';
import { useReadOnlyReason } from '../../ui/ReadOnly';
import {
  Act,
  PLATFORM_SCOPE,
  ReadOnlyAs,
  ageText,
  answersHeld,
  durText,
  guardLine,
  isFacilityAdmin,
  isOperatorTier,
  residentLine,
  useApi,
  useEventFeed,
  useNow,
  useShellFit,
  utcClock,
  verdictTag,
} from './common';
import type { LedgerIntent, OversightTab } from './OversightPage';
import { BuildTagged } from '../../ui/buildLabels';
import { ackerName } from '../pulse/acker';

/**
 * The chain head a verify reached, as the design words it ("Head 3f9a0c21b7e4…"). The verify
 * returns headHash; when nothing in the range was proved it is null and the line names the
 * head by its sequence number, or says nothing.
 */
function headOf(r: LedgerRangeVerification): string {
  if (r.headHash) return ' Head ' + r.headHash.slice(0, 12) + '…';
  return r.lastSeq ? ' Head #' + r.lastSeq + '.' : '';
}

type Range = 'LIVE' | '1H' | '24H' | '7D';
const RANGES: Range[] = ['LIVE', '1H', '24H', '7D'];
const RANGE_QUERY: Record<Range, { windowMinutes?: number; windowHours?: number }> = {
  LIVE: { windowMinutes: 5 },
  '1H': { windowMinutes: 60 },
  '24H': { windowHours: 24 },
  '7D': { windowHours: 168 },
};

// design monV2: VC, SC
const VC: Record<string, [string, string]> = { ALLOW: ['#1F6B52', 'rgba(31,107,82,.12)'], HOLD: ['#264C7E', 'rgba(47,92,151,.12)'], BLOCK: ['#B3261E', 'rgba(179,38,30,.12)'], ALERT: ['#B3261E', 'rgba(179,38,30,.12)'] };
type State = 'NORMAL' | 'WATCH' | 'DEGRADED' | 'HALTED';
// The pill wears the side-nav label's tone for the same state: System Pulse's Outage is red,
// so DEGRADED is red; Degraded and Watch are amber, so WATCH is amber (it was a grey-blue
// DEGRADED beside a red Outage in the menu).
const SC: Record<State, [string, string]> = {
  NORMAL: ['#1F6B52', 'rgba(31,107,82,.12)'],
  WATCH: ['var(--tone-amber-fg)', 'var(--tone-amber-bg)'],
  DEGRADED: ['var(--tone-red-fg)', 'var(--tone-red-bg)'],
  HALTED: ['#B3261E', 'rgba(179,38,30,.12)'],
};

const H14 = 'font-size:16px;font-weight:800;color:#22272E;letter-spacing:-.01em;white-space:nowrap;';
const HEAD = 'display:flex;align-items:baseline;gap:4px 8px;flex-wrap:wrap;min-height:26px;';
const BLOCK = 'display:flex;flex-direction:column;gap:3px;min-width:0;margin-bottom:14px;';
const TIP = 'margin-left:auto;font-size:16px;font-weight:700;color:#2F5C97;text-decoration:none;white-space:nowrap;';

/** One sparkline, 64x24, from a trace (design monV2 stats: d, ey). */
function tracePath(t: OversightTrace | undefined): { d: string; ey: string } | null {
  if (!t || t.points.length < 2) return null;
  const pts = t.points;
  const lo = t.scale ? t.scale.lo : Math.min(...pts);
  const hi = t.scale ? t.scale.hi : Math.max(...pts);
  const norm = (v: number) => (hi === lo ? 0.5 : (v - lo) / (hi - lo));
  const Y = (p: number) => (22 - Math.max(0, Math.min(1, p)) * 20).toFixed(1);
  const n = pts.length - 1;
  return { d: pts.map((v, j) => (j ? 'L' : 'M') + ((j * 64) / n).toFixed(1) + ' ' + Y(norm(v))).join(' '), ey: Y(norm(pts[n])) };
}

type Pending =
  | { kind: 'held'; item: OversightHeldItem; decision: OversightHeldDecision; label: string }
  | { kind: 'ack'; inc: PulseIncident }
  | { kind: 'kill'; resume: boolean }
  | { kind: 'pause'; paused: boolean };

export function Summary({ onGo }: { onGo: (t: OversightTab, i?: LedgerIntent) => void }) {
  const { role } = useConsoleRole();
  const { session, client } = useSession();
  const roReason = useReadOnlyReason();
  const fa = isFacilityAdmin(role);
  const op = !fa;
  const canAct = answersHeld(role);
  const canKill = isOperatorTier(role);
  // The owner tier (Super Admin) is the only one who turns the AI back on (design aiTier).
  const isOwner = role === 'SUPER_ADMIN';
  const fit = useShellFit();
  const { tight, short, shortL, rw } = fit;
  const now = useNow(1000);
  const [range, setRange] = useState<Range>('LIVE');
  const [paused, setPaused] = useState(false);
  const [hover, setHover] = useState(false);
  const [pin, setPin] = useState<OversightEvent | null>(null);
  const [ver, setVer] = useState<{ s: 'running' } | { s: 'done'; r: LedgerRangeVerification } | { s: 'error'; msg: string } | null>(null);
  const [pending, setPending] = useState<Pending | null>(null);
  const [decided, setDecided] = useState<Record<string, string>>({});
  const orgId = session?.user.organizationId ?? null;

  const posture = useApi((c, signal) => policyPosture(c, { query: fa ? {} : { scope: 'network' }, signal }), [fa]);
  const held = useApi((c, signal) => oversightHeld(c, { query: { limit: 100 }, signal }), [], { pollMs: 15000 });
  const guards = useApi((c, signal) => oversightGuards(c, { signal }), [], { pollMs: 15000 });
  // Pulse incidents are Aithora's own system incidents (operator access); a Facility Admin's
  // emergencies and reports live on Guardian. Asking anyway was a 403 on every visit.
  const incidents = useApi((c, signal) => pulseIncidents(c, { query: { windowDays: 7 }, signal }), [], { pollMs: 30000, enabled: !fa });
  const mon = useApi((c, signal) => metricsMonitoring(c, { query: RANGE_QUERY[range], signal }), [range], { pollMs: 15000 });
  const tape = useEventFeed({ windowMs: 15 * 60_000, limit: 200 });
  const traces = useApi((c, signal) => oversightTraces(c, { query: { points: 24 }, signal }), [], { pollMs: 15000 });
  const kills = useApi((c, signal) => killSwitches(c, { signal }), [], { enabled: canKill });
  const human = useApi((c, signal) => oversightHuman(c, { query: { window: '24h' }, signal }), []);
  const reviewers = useApi((c, signal) => oversightReviewers(c, { query: { window: '24h' }, signal }), []);
  const thread = useApi((c, signal) => oversightThread(c, { signal }), []);
  const building = useApi((c, signal) => pulseV3Facilities(c, { signal }), [], { enabled: fa });
  const pause = useApi((c, signal) => aiPauseState(c, { params: { id: orgId ?? '' }, signal }), [orgId], { enabled: fa && !!orgId });
  const alerts = useApi((c, signal) => bell(c, { query: { view: 'bell', limit: 20 }, signal }), [], { enabled: op });
  const badges = useNavBadges();

  const killed = !!kills.data?.killSwitches.some((k) => k.active);
  const bPaused = !!pause.data?.paused;

  // ── Guards: the design's count per width (10, 6 when tight or short, 5 under 1400) ──
  const G = useMemo(
    () =>
      (guards.data?.guards ?? []).slice(0, rw < 1400 ? 5 : tight || short ? 6 : 10).map((g) => {
        const cur = g.loadPct;
        const hot = cur / g.tripPct >= 0.7;
        return { key: g.key, name: g.name, lvl: 'L' + g.level, pct: Math.round(cur) + '%', trip: g.tripPct + '%', hot, ratio: cur / g.tripPct, cur };
      }),
    [guards.data, rw, tight, short],
  );
  const nearG = G.filter((g) => g.hot).sort((a, b) => b.ratio - a.ratio);

  const heldItems = (held.data?.items ?? []).filter((h) => !decided[h.id]);
  const heldN = held.data ? heldItems.length : null;
  const waiting = heldN === null ? '–' : String(heldN);

  // ── The state line (worst state wins; a degraded channel is never hidden) ──
  // System Pulse's own menu label (GET /nav/badges pulse, the same building cards the Pulse
  // page draws) is part of the state: "NORMAL, every channel is fresh" beside a System
  // Pulse outage was two pages disagreeing about one system. Red (Outage) is DEGRADED,
  // amber (Degraded or Watch) is WATCH; NORMAL only when Pulse also reads all good.
  const pulseB = badges.data?.badges.pulse ?? null;
  const pulseDown = pulseB?.tone === 'red';
  const pulseWatch = pulseB?.tone === 'amber';
  // "System Pulse: 2 outages", never "outage, 2 outages": the count already names the state.
  const pulseSentence = pulseDown || pulseWatch ? 'System Pulse: ' + (pulseB!.aria || (pulseB!.label ?? '').toLowerCase()) : '';
  const degraded = posture.data?.posture === 'DEGRADED';
  const state: State = killed || posture.data?.posture === 'HALTED' ? 'HALTED' : degraded || pulseDown ? 'DEGRADED' : nearG.length || pulseWatch ? 'WATCH' : 'NORMAL';
  const guardSentence = nearG.length ? nearG[0].name + ' is at ' + nearG[0].pct + ' of its trip line' : '';
  const stateLine =
    fa && bPaused
      ? 'AI actions paused in your building: every action is held for a person. Atlas keeps talking. ' + waiting + ' waiting for a person.'
      : killed
        ? 'Kill switch engaged: AI generation frozen. Safety escalation and 911 stay live.'
        : degraded
          ? (posture.data?.reason ? posture.data.reason + ' ' : '') + (guardSentence ? guardSentence + '. ' : '') + (pulseSentence ? pulseSentence + '. ' : '') + waiting + ' waiting for a person.'
          : pulseSentence
            ? pulseSentence + '. ' + (guardSentence ? guardSentence + '. ' : '') + waiting + ' waiting for a person.'
            : guardSentence
              ? guardSentence + '; ' + waiting + ' waiting for a person.'
              : (fa ? 'Your building is working, ' : 'Every channel is fresh, ') + waiting + ' waiting for a person.';
  const sc = SC[state];

  // ── The tape: the sixteen newest events, newest first ──
  // Paused or under the pointer, the tape holds still while the feed keeps arriving.
  const frozenTape = useRef<OversightEvent[] | null>(null);
  if (!(paused || hover)) frozenTape.current = null;
  else if (!frozenTape.current) frozenTape.current = tape.events;
  const shownTape = (paused || hover) && frozenTape.current ? frozenTape.current : tape.events;
  const tapeRows = useMemo(() => [...(shownTape ?? [])].reverse().slice(0, 16), [shownTape]);
  const topId = tapeRows[0]?.id;

  // ── The five counts ──
  const tr = traces.data;
  const p50 = mon.data?.latency.p50;
  const stats = [
    { k: 'events / min', v: tr?.eventsPerMin.now ?? null, c: '#22272E', note: 'every event type on the tape', t: tr?.eventsPerMin, go: null as OversightTab | null },
    { k: 'gate p50', v: p50 ?? null, unit: 'ms', c: '#22272E', note: 'decision latency', t: tr?.gateP50Ms, go: 'live' as OversightTab },
    { k: 'waiting for a person', v: heldN, c: heldN ? '#B3261E' : '#22272E', note: 'held decisions', t: tr?.held, go: 'live' as OversightTab },
    { k: 'guards near limit', v: guards.data ? nearG.length : null, c: nearG.length ? '#B3261E' : '#22272E', note: '70% of trip line or more', t: tr?.nearLimit, go: null },
    { k: 'active threats', v: killed ? 'halted' : (tr?.threats.now ?? null), c: killed ? '#B3261E' : '#22272E', note: '', t: tr?.threats, go: 'ledger' as OversightTab },
  ];

  // ── Incidents & scams: open and acknowledged, never resolved ──
  // Aithora's own platform incidents are for Super Admin and Developer only (Andrew,
  // 2026-10-19; the server holds every other role to facility incidents too).
  const seesPlatform = role === 'SUPER_ADMIN' || role === 'DEVELOPER';
  const incList = (incidents.data?.data.incidents ?? []).filter((i) => i.status !== 'resolved' && (seesPlatform || i.scope === 'facility' || (i.scope === 'anomaly' && String(i.signal ?? '').startsWith('facility:'))));
  const incOpen = incList.filter((i) => i.status === 'open').length;
  const incShown = tight || short ? 2 : 3;

  // ── Decision path and verdicts ──
  const layers = mon.data?.decisionPath?.layers ?? [];
  const vm = mon.data?.verdicts;
  const vb: [string, number, string][] = vm ? [['allow', vm.approved, '#1F6B52'], ['hold', vm.held, '#B3261E'], ['block', vm.blocked, '#B3261E']] : [];
  const vSum = vb.reduce((a, x) => a + x[1], 0);

  // ── Right column ──
  const pb = badges.data?.badges.pulse;
  const pulseC = !pb ? '#54606C' : pb.label === 'Outage' ? '#B3261E' : pb.label !== 'All good' ? '#8A5A00' : '#1F6B52';
  const pulseBg = !pb ? 'rgba(34,39,46,.05)' : pb.label === 'Outage' ? 'rgba(179,38,30,.08)' : pb.label !== 'All good' ? 'rgba(184,134,11,.10)' : 'rgba(47,138,111,.08)';
  const bldAlerts = (alerts.data?.items ?? []).filter((a) => a.type === 'OVERSIGHT_BUILDING');
  const card = building.data?.facilities[0];
  const bldName = card?.name ?? session?.user.orgName ?? 'Your building';
  const LINK_WORD: Record<string, [string, string]> = { ok: ['WORKING', '#1F6B52'], degraded: ['CHECK', '#8A5A00'], down: ['DOWN', '#B3261E'], unknown: ['UNKNOWN', '#54606C'], na: ['NOT SET UP', '#54606C'] };
  const bld = card
    ? ([['Atlas reaching residents', card.links.companion], ['Pendants and sensors', card.links.sensors], ['Chart connection', card.links.ehr_feed]] as const).map(([name, l]) => {
        const w = LINK_WORD[l?.state ?? 'unknown'] ?? LINK_WORD.unknown;
        return { name, meta: l?.value ?? 'Not reported', state: w[0], c: w[1] };
      })
    : [];

  const hu = human.data;
  const pct = (r: number | null | undefined) => (r === null || r === undefined ? '–' : Math.round(r * 100) + '%');
  const hoStats = [
    { v: hu ? durText(hu.medianTimeToPersonSec) : '–', k: 'time to a person', tip: 'Typical time from an item being held to a person deciding it' },
    { v: hu ? pct(hu.overruleRate) : '–', k: 'overruled by a person', tip: 'Share of held items where the person decided differently from the gate' },
    { v: hu ? String(hu.activeReviewers) : '–', k: 'reviewers active', tip: 'People who decided a held item in the last 24h' },
  ];
  const hoGates = [...(hu?.perGate ?? [])]
    .filter((g) => g.overruleRate !== null)
    .sort((a, b) => (b.overruleRate ?? 0) - (a.overruleRate ?? 0))
    .slice(0, 4)
    .map((g) => {
      const n = Math.round((g.overruleRate ?? 0) * 100);
      return { name: g.name || g.gate, pct: n + '%', c: n >= 15 ? '#8A5A00' : '#22272E', bar: 'display:block;height:100%;border-radius:3px;width:' + Math.min(100, n * 4) + '%;background:' + (n >= 15 ? '#B8860B' : '#2F5C97') + ';', tip: 'People overruled this gate on ' + n + '% of what it held. A high rate means it holds things people keep releasing: tune it in Policy.' };
    });

  const steps = thread.data?.thread?.steps ?? [];
  const nowIdx = Math.max(0, steps.findIndex((s) => s.state === 'now'));
  const shownSteps = short ? steps.filter((_, i) => i === nowIdx) : steps;

  // ── Writes (confirm first) ──
  const verify = async () => {
    setVer({ s: 'running' });
    const from = new Date(now);
    from.setUTCHours(0, 0, 0, 0);
    try {
      const r = await ledgerVerify(client, { body: { ...(fa ? {} : { scope: PLATFORM_SCOPE }), from: from.toISOString(), to: new Date().toISOString() } });
      setVer({ s: 'done', r });
    } catch (e) {
      setVer({ s: 'error', msg: (e as Error).message });
    }
  };

  const confirmBody = (): { title: string; body: string; label: string; danger?: boolean; reason?: { placeholder: string; required: boolean }; run: (reason?: string) => Promise<void> } | null => {
    if (!pending) return null;
    if (pending.kind === 'held') {
      const who = residentLine(pending.item.resident) ?? 'this item';
      return {
        title: pending.label + ': ' + who,
        body: 'Your decision is sealed to the Truth Ledger with your name and the time.' + (fa ? ' Aithora is alerted.' : ''),
        label: pending.label,
        danger: pending.decision === 'refuse',
        run: async () => {
          await oversightHeldDecide(client, { params: { id: pending.item.id }, body: withConfirm({ decision: pending.decision }) });
          setDecided((d) => ({ ...d, [pending.item.id]: pending.decision }));
          held.reload();
        },
      };
    }
    if (pending.kind === 'ack') {
      return {
        title: 'Acknowledge: ' + pending.inc.title,
        body: 'Acknowledging stops the escalation and records you as the owner.',
        label: 'Acknowledge',
        run: async () => {
          await pulseIncidentAck(client, { params: { id: pending.inc.id }, body: { confirm: true } });
          incidents.reload();
        },
      };
    }
    if (pending.kind === 'kill') {
      // One stop for the AI (handoff V4 §16 A): this button and the top bar's Pause AI drive
      // the same tenant-wide switch. Only the owner turns it back on; the server refuses
      // anyone else.
      return pending.resume
        ? {
            title: 'Turn the AI back on?',
            body: 'Atlas goes back to acting within each role’s approvals. Anything approved while it was paused runs now.',
            label: 'Turn AI back on',
            run: async () => {
              await killDeactivate(client, { body: withConfirm({ all: true as const }) });
              kills.reload();
            },
          }
        : {
            title: 'Pause the AI?',
            body: 'Atlas stops taking any action, in every building. Answers and Atlas keep working. Only Aithora can turn it back on.',
            label: 'Pause now',
            danger: true,
            reason: { placeholder: 'Reason (optional)', required: false },
            run: async (reason?: string) => {
              await killActivate(client, { body: withConfirm({ scope: 'global' as const, reason: reason || 'Paused from Oversight' }) });
              kills.reload();
            },
          };
    }
    return pending.paused
      ? {
          title: 'Pause AI actions in ' + bldName + '?',
          body: 'Every AI action here is held for a person. Atlas keeps talking. Aithora is alerted and on-call is paged.',
          label: 'Pause AI actions',
          danger: true,
          run: async () => {
            if (orgId) await aiPauseSet(client, { params: { id: orgId }, body: withConfirm({ paused: true }) });
            pause.reload();
          },
        }
      : {
          title: 'Resume AI actions in ' + bldName + '?',
          body: 'AI actions go back to the gate. Aithora is alerted.',
          label: 'Resume AI actions',
          run: async () => {
            if (orgId) await aiPauseSet(client, { params: { id: orgId }, body: withConfirm({ paused: false }) });
            pause.reload();
          },
        };
  };
  const dlg = confirmBody();

  const banner =
    ver === null
      ? null
      : ver.s === 'running'
        ? { tag: 'VERIFYING', text: 'Recomputing every hash from the first entry today to the head…', ok: false }
        : ver.s === 'error'
          ? { tag: 'NOT VERIFIED', text: ver.msg, ok: false }
          : ver.r.valid
            ? { tag: 'CHAIN INTACT', text: 'Verified ' + ver.r.checked.toLocaleString() + ' entries, 00:00 UTC to now. Every hash matches its predecessor.' + headOf(ver.r), ok: true }
            : { tag: 'BREAK FOUND', text: 'Entry #' + (ver.r.brokenAt?.seq ?? '?') + ' does not match what was written. ' + (ver.r.reason ?? ''), ok: false };

  const HELD_SHOWN = tight || shortL ? 3 : 4;
  const heldMore = (tight || shortL) && heldItems.length > 3;
  const decideLabel = { allow: 'Allow', refuse: 'Refuse', route: 'Route' } as const;

  return (
    <div data-screen-label="Monitoring · Live" style={sx('height:100%;min-height:0;display:flex;flex-direction:column;gap:9px;min-width:0;')}>
      {/* State, clock, ranges and the page's actions */}
      <div style={sx('flex:0 0 auto;display:flex;align-items:center;gap:8px 10px;flex-wrap:wrap;min-width:0;')}>
        <span data-testid="ov-state" style={sx('display:inline-flex;align-items:center;gap:7px;height:30px;padding:0 12px;border-radius:8px;font-size:16px;font-weight:800;letter-spacing:.1em;white-space:nowrap;color:' + sc[0] + ';background:' + sc[1] + ';')}>
          <span style={sx('width:8px;height:8px;border-radius:50%;background:' + sc[0] + ';animation:breathe 1.6s ease-in-out infinite;')} />
          {state}
        </span>
        <span title={stateLine} style={sx('font-size:16px;font-weight:600;color:#22272E;flex:1 1 260px;min-width:0;line-height:1.35;text-wrap:pretty;')}>
          {stateLine}
        </span>
        <span style={sx('font-size:16px;font-weight:800;font-variant-numeric:tabular-nums;color:#22272E;white-space:nowrap;')}>{(paused ? 'PAUSED · ' : '') + utcClock(new Date(now)) + ' UTC'}</span>
        <span role="group" aria-label="Range" style={sx('display:inline-flex;border:1px solid rgba(34,39,46,.16);border-radius:9px;overflow:hidden;')}>
          {RANGES.map((r) => (
            <Act key={r} pressed={range === r} onClick={() => setRange(r)} css={'display:inline-flex;align-items:center;min-height:34px;padding:0 11px;font-size:14px;font-weight:700;text-decoration:none;' + (range === r ? 'background:#22272E;color:#FCFAF4;' : 'color:#3C454F;')}>
              {r}
            </Act>
          ))}
        </span>
        <Act onClick={() => setPaused((p) => !p)} css={'display:inline-flex;align-items:center;min-height:36px;padding:0 12px;border-radius:9px;font-size:16px;font-weight:700;text-decoration:none;white-space:nowrap;' + (paused ? 'background:#22272E;color:#FCFAF4;' : 'color:#2F5C97;padding:0 2px;')}>
          {paused ? 'Resume' : 'Pause'}
        </Act>
        <Act onClick={verify} css="display:inline-flex;align-items:center;min-height:36px;padding:0 2px;color:#2F5C97;font-size:16px;font-weight:700;text-decoration:none;white-space:nowrap;">
          Verify chain
        </Act>
        <Act onClick={() => onGo('ledger', { exportOpen: true })} css="display:inline-flex;align-items:center;min-height:36px;padding:0 2px;color:#2F5C97;font-size:16px;font-weight:700;text-decoration:none;white-space:nowrap;">
          Export evidence
        </Act>
        {canKill && (
          <Act
            onClick={killed && !isOwner ? undefined : () => setPending({ kind: 'kill', resume: killed })}
            disabled={!!roReason}
            title={roReason ?? (killed && !isOwner ? 'Only Aithora can turn it back on.' : undefined)}
            css={'display:inline-flex;align-items:center;min-height:36px;padding:0 12px;border-radius:9px;font-size:16px;font-weight:800;letter-spacing:.06em;text-decoration:none;white-space:nowrap;' + (killed ? 'background:#1F6B52;color:#fff;' : 'background:#B3261E;color:#fff;')}
          >
            {killed ? (isOwner ? 'TURN AI BACK ON' : 'PAUSED · AITHORA RESTARTS') : 'PAUSE AI'}
          </Act>
        )}
      </div>

      {/* The tape */}
      <div
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        style={sx('flex:0 0 auto;display:flex;align-items:stretch;gap:0;min-width:0;height:36px;border-top:1px solid rgba(34,39,46,.14);border-bottom:1px solid rgba(34,39,46,.14);overflow:hidden;')}
      >
        <span style={sx('flex:0 0 auto;display:flex;align-items:center;gap:8px;padding:0 12px 0 2px;border-right:1px solid rgba(34,39,46,.10);white-space:nowrap;')}>
          <span style={sx('display:inline-flex;align-items:center;gap:6px;font-size:14px;font-weight:800;letter-spacing:.1em;color:' + (paused || hover ? '#B3261E' : '#1F6B52') + ';')}>
            <span style={sx('width:7px;height:7px;border-radius:50%;background:' + (paused || hover ? '#B3261E' : '#1F6B52') + ';' + (paused ? '' : 'animation:breathe 1.4s ease-in-out infinite;'))} />
            {paused ? 'PAUSED' : hover ? 'HELD' : 'LIVE'}
          </span>
          <span style={sx('font-size:14px;font-weight:700;letter-spacing:.06em;color:#54606C;')}>{(tr?.eventsPerMin.now ?? '–') + ' events/min'}</span>
        </span>
        <div role="log" aria-label="Every event, newest first" style={sx('flex:1 1 auto;min-width:0;display:flex;align-items:stretch;overflow:hidden;-webkit-mask-image:linear-gradient(90deg,#000 92%,transparent);mask-image:linear-gradient(90deg,#000 92%,transparent);')}>
          {tape.events && tapeRows.length === 0 && <span style={sx('display:flex;align-items:center;padding:0 14px;font-size:14px;color:#54606C;')}>No events in the last 15 minutes.</span>}
          {tapeRows.map((e) => {
            const v = verdictTag(e);
            const vc = VC[v] ?? ['#264C7E', 'rgba(47,92,151,.10)'];
            const meta = [e.facility?.name, guardLine(e), e.latencyMs !== null ? Math.round(e.latencyMs) + 'ms' : null].filter(Boolean).join(' · ');
            return (
              <Act
                key={e.id}
                onClick={() => setPin(e)}
                title={(e.ref ?? e.id) + ' · click to pin'}
                css={
                  'flex:0 0 auto;display:flex;align-items:center;gap:7px;padding:0 14px;border-right:1px solid rgba(34,39,46,.08);text-decoration:none;color:inherit;' +
                  (pin?.id === e.id ? 'background:rgba(47,92,151,.10);' : '') +
                  (e.id === topId ? 'animation:slideIn .45s cubic-bezier(.22,.61,.36,1);' : '') +
                  (v === 'HOLD' || v === 'BLOCK' || v === 'ALERT' ? 'box-shadow:inset 0 3px 0 ' + vc[0] + ';' : '')
                }
              >
                <span style={sx('font-size:14px;font-variant-numeric:tabular-nums;color:#54606C;white-space:nowrap;')}>{utcClock(new Date(e.at))}</span>
                <span style={sx('font-size:14px;font-weight:800;letter-spacing:.07em;padding:0 5px;border-radius:3px;color:' + vc[0] + ';background:' + vc[1] + ';')}>{v}</span>
                <span style={sx('font-size:16px;font-weight:700;color:#22272E;white-space:nowrap;')}>{e.summary ?? e.type}</span>
                <span style={sx('font-size:14px;color:#54606C;white-space:nowrap;')}>{meta}</span>
              </Act>
            );
          })}
        </div>
      </div>

      {pin && (
        <div style={sx('flex:0 0 auto;display:flex;align-items:center;gap:6px 18px;flex-wrap:wrap;min-width:0;padding:8px 12px;border-radius:10px;background:rgba(47,92,151,.07);animation:riseIn .2s cubic-bezier(.22,.61,.36,1);')}>
          <span style={sx('font-size:14px;font-weight:800;letter-spacing:.1em;color:#264C7E;')}>PINNED</span>
          {[
            ['ENTRY', pin.ref ?? pin.ledgerEntryId ?? pin.id],
            ['AT', utcClock(new Date(pin.at)) + ' UTC'],
            ['WHAT', [pin.summary ?? pin.type, pin.facility?.name].filter(Boolean).join(' · ')],
            ['HASH', pin.ledgerHash ? pin.ledgerHash.slice(0, 12) + '…' : 'not sealed'],
            ...(pin.ledgerPreviousHash ? [['PREVIOUS', pin.ledgerPreviousHash.slice(0, 12) + '…']] : []),
            ['THREAD', pin.threadId ?? '–'],
          ].map(([k, v]) => (
            <span key={k} style={sx('display:inline-flex;flex-direction:column;min-width:0;')}>
              <span style={sx('font-size:14px;font-weight:700;letter-spacing:.08em;color:#54606C;')}>{k}</span>
              <span style={sx('font-size:16px;font-weight:700;color:#22272E;font-variant-numeric:tabular-nums;white-space:nowrap;')}>{v}</span>
            </span>
          ))}
          <span style={sx('flex:1 1 10px;')} />
          <Act onClick={() => onGo('ledger', { query: pin.ref ?? pin.ledgerEntryId ?? '' })} css="font-size:16px;font-weight:700;color:#2F5C97;text-decoration:none;white-space:nowrap;">
            Open in Truth Ledger →
          </Act>
          <Act onClick={() => setPin(null)} css="font-size:16px;font-weight:700;color:#54606C;text-decoration:none;">
            Unpin
          </Act>
        </div>
      )}
      {banner && (
        <div role="status" style={sx('flex:0 0 auto;display:flex;align-items:center;gap:6px 14px;flex-wrap:wrap;padding:8px 12px;border-radius:10px;animation:riseIn .2s cubic-bezier(.22,.61,.36,1);' + (banner.ok ? 'background:rgba(31,107,82,.10);color:#1F6B52;' : 'background:rgba(47,92,151,.08);color:#264C7E;'))}>
          <BuildTagged tag={banner.tag}><span style={sx('font-size:14px;font-weight:800;letter-spacing:.08em;')}>{banner.tag}</span></BuildTagged>
          <span style={sx('font-size:16px;font-weight:600;color:#22272E;flex:1 1 260px;min-width:0;line-height:1.4;text-wrap:pretty;')}>{banner.text}</span>
          <Act onClick={() => setVer(null)} css="font-size:16px;font-weight:700;color:#54606C;text-decoration:none;">
            Close
          </Act>
        </div>
      )}

      {/* The five counts, each with its trace */}
      <div style={sx('flex:0 0 auto;display:grid;grid-template-columns:repeat(auto-fit,minmax(150px,1fr));gap:4px 24px;min-width:0;')}>
        {stats.map((t) => {
          const path = tracePath(t.t);
          const sc2 = t.c === '#B3261E' ? '#B3261E' : '#2F5C97';
          const val = t.v === null || t.v === undefined ? '–' : typeof t.v === 'number' ? String(Math.round(t.v)) + (t.unit ?? '') : t.v;
          return (
            <Act key={t.k} onClick={t.go ? () => onGo(t.go as OversightTab) : undefined} title={t.note} css="display:flex;align-items:center;gap:10px;min-width:0;text-decoration:none;padding:2px 0;">
              <span style={sx('display:flex;flex-direction:column;min-width:0;')}>
                <span style={sx('font-size:22px;font-weight:800;letter-spacing:-.03em;font-variant-numeric:tabular-nums;color:' + t.c + ';transition:color .3s;line-height:1.1;')}>{val}</span>
                {/* Wraps rather than spilling past its column ("waiting for a person" at laptop widths). */}
                <span style={sx('font-size:16px;color:#3C454F;line-height:1.25;overflow-wrap:anywhere;text-wrap:balance;')}>{t.k}</span>
              </span>
              <svg width="64" height="24" viewBox="0 0 64 24" style={sx('flex:0 0 auto;margin-left:auto;overflow:visible;')} aria-hidden="true">
                {path && <path d={path.d} fill="none" stroke={sc2} strokeWidth="1.6" strokeLinejoin="round" strokeLinecap="round" />}
                {path && <circle cx="64" cy={path.ey} r="2.4" fill={sc2} />}
              </svg>
            </Act>
          );
        })}
      </div>

      <div style={sx('flex:1 1 auto;min-height:0;overflow-y:auto;overflow-x:hidden;min-width:0;padding-right:3px;border-top:1px solid rgba(34,39,46,.12);padding-top:10px;')}>
        <div style={sx('display:grid;grid-template-columns:repeat(auto-fit,minmax(290px,1fr));gap:4px 28px;align-items:start;min-width:0;')}>
          {/* Column 1: held items, incidents */}
          <div style={sx('display:flex;flex-direction:column;min-width:0;')}>
            <section aria-label="Needs you now" style={sx(BLOCK)}>
              <span style={sx(HEAD)}>
                <span style={sx(H14)}>Needs you now</span>
                <span data-testid="ov-waiting" style={sx('font-size:16px;font-weight:800;color:' + (heldN ? '#B3261E' : '#54606C') + ';font-variant-numeric:tabular-nums;white-space:nowrap;')}>{waiting} waiting</span>
                {heldMore && (
                  <Act onClick={() => onGo('live')} css={TIP}>
                    {'+' + (heldItems.length - 3) + ' more →'}
                  </Act>
                )}
              </span>
              {held.data && heldItems.length === 0 && <span style={sx('font-size:16px;color:#54606C;padding:8px 0;')}>Nothing is waiting. Every held decision has an answer.</span>}
              {held.error && <span style={sx('font-size:16px;color:#54606C;padding:8px 0;')}>{held.error.message}</span>}
              {heldItems.slice(0, HELD_SHOWN).map((h) => {
                const sec = (now - Date.parse(h.surfacedAt)) / 1000;
                const ro = !!roReason;
                return (
                  <span key={h.id} style={sx('display:flex;flex-direction:column;gap:4px;padding:8px 0;border-top:1px solid rgba(34,39,46,.08);min-width:0;')}>
                    <span style={sx('display:flex;align-items:baseline;gap:4px 8px;flex-wrap:wrap;min-width:0;')}>
                      <b style={sx('font-size:16px;color:#22272E;white-space:nowrap;line-height:1.35;')}>{residentLine(h.resident) ?? h.facility?.name ?? 'AI action'}</b>
                      <span style={sx('font-size:14px;color:#54606C;white-space:nowrap;line-height:1.35;')}>{h.reason ?? h.gate}</span>
                      <span style={sx('font-size:16px;font-weight:800;font-variant-numeric:tabular-nums;color:' + (sec >= 15 * 60 ? '#B3261E' : '#22272E') + ';white-space:nowrap;margin-left:auto;')}>{ageText(sec)} waiting</span>
                    </span>
                    {h.summary && <span style={sx('font-size:16px;color:#3C454F;line-height:1.4;text-wrap:pretty;')}>{h.summary}</span>}
                    {canAct ? (
                      <span style={sx('display:flex;align-items:center;gap:6px 16px;flex-wrap:wrap;')}>
                        <Act disabled={ro} title={roReason ?? undefined} onClick={() => setPending({ kind: 'held', item: h, decision: 'allow', label: decideLabel.allow })} css="display:inline-flex;align-items:center;min-height:36px;padding:0 14px;border-radius:8px;background:#1F6B52;color:#fff;font-size:16px;font-weight:700;text-decoration:none;white-space:nowrap;">
                          Allow
                        </Act>
                        <Act disabled={ro} title={roReason ?? undefined} onClick={() => setPending({ kind: 'held', item: h, decision: 'refuse', label: decideLabel.refuse })} css="display:inline-flex;align-items:center;min-height:36px;padding:0 2px;font-size:16px;font-weight:700;text-decoration:none;white-space:nowrap;color:#B3261E;">
                          Refuse
                        </Act>
                        <Act disabled={ro || h.routed} title={roReason ?? (h.routed ? 'Already sent to the nurse' : undefined)} onClick={() => setPending({ kind: 'held', item: h, decision: 'route', label: h.routeLabel ?? 'Send to nurse' })} css="display:inline-flex;align-items:center;min-height:36px;padding:0 2px;font-size:16px;font-weight:700;text-decoration:none;white-space:nowrap;color:#2F5C97;">
                          {h.routeLabel ?? 'Send to nurse'}
                        </Act>
                      </span>
                    ) : (
                      <ReadOnlyAs role={role} />
                    )}
                  </span>
                );
              })}
            </section>
            <section aria-label="Incidents and scams" style={sx(BLOCK)}>
              <span style={sx(HEAD)}>
                <span style={sx(H14)}>Incidents &amp; scams</span>
                <span style={sx('font-size:16px;font-weight:800;color:#B3261E;font-variant-numeric:tabular-nums;')}>{fa ? '' : incidents.data ? incOpen + ' open' : '–'}</span>
                {/* The block shows the newest two or three: every one is a click away on Pulse. */}
                {!fa && incList.length > incShown && (
                  <Link to="/pulse?tab=Incidents" data-testid="ov-incidents-all" style={sx(TIP)}>
                    {'See all ' + incList.length + ' →'}
                  </Link>
                )}
              </span>
              {fa && (
                <Link to="/guardian" style={sx('font-size:16px;color:#2F5C97;padding:8px 0;font-weight:600;text-decoration:none;')}>
                  Your building's emergencies and reports are on Guardian →
                </Link>
              )}
              {incidents.data && incList.length === 0 && <span style={sx('font-size:16px;color:#54606C;padding:8px 0;')}>No open incidents.</span>}
              {incList.slice(0, incShown).map((i) => {
                const acked = i.status === 'acknowledged';
                const c = acked ? '#54606C' : '#B3261E';
                const meta = typeof i.detail?.summary === 'string' ? (i.detail.summary as string) : i.signal;
                return (
                  <span key={i.id} style={sx('display:flex;align-items:center;gap:10px;padding:7px 0;border-top:1px solid rgba(34,39,46,.08);min-width:0;box-shadow:inset 3px 0 0 ' + c + ';padding-left:10px;')}>
                    <span style={sx('display:flex;flex-direction:column;min-width:0;flex:1 1 auto;')}>
                      <span style={sx('font-size:16px;font-weight:700;color:' + c + ';')}>{plainProblemTitle(i.title)}</span>
                      <span style={sx('font-size:14px;color:#54606C;line-height:1.35;')}>{meta}</span>
                    </span>
                    <span style={sx('display:flex;flex-direction:column;align-items:flex-end;gap:4px;flex:0 0 auto;')}>
                      <span style={sx('font-size:14px;font-weight:800;font-variant-numeric:tabular-nums;color:#22272E;white-space:nowrap;')}>{ageText((now - Date.parse(i.openedAt)) / 1000)}</span>
                      <Act
                        disabled={acked || !!roReason}
                        title={roReason ?? undefined}
                        onClick={() => setPending({ kind: 'ack', inc: i })}
                        css={'display:inline-flex;align-items:center;min-height:32px;padding:0 10px;border-radius:8px;font-size:14px;font-weight:700;text-decoration:none;white-space:nowrap;' + (acked ? 'color:#1F6B52;' : 'color:#2F5C97;padding:0 2px;')}
                      >
                        {acked ? '✓ ' + (i.acknowledgedBy && i.acknowledgedBy === session?.user.id ? 'You' : i.acknowledgedBy ? ackerName(i) : 'Acknowledged') : 'Acknowledge'}
                      </Act>
                    </span>
                  </span>
                );
              })}
            </section>
          </div>

          {/* Column 2: guard headroom, decision path, verdicts */}
          <div style={sx('display:flex;flex-direction:column;min-width:0;')}>
            <section aria-label="Guard headroom" style={sx(BLOCK)}>
              <span style={sx(HEAD)}>
                <span style={sx(H14)}>Guard headroom</span>
                <Link to="/policy" style={sx(TIP)}>
                  Change in Policy →
                </Link>
              </span>
              {guards.error && <span style={sx('font-size:16px;color:#54606C;padding:8px 0;')}>Guard load is not served yet.</span>}
              {G.map((g) => (
                <span key={g.key} style={sx('display:grid;grid-template-columns:minmax(0,1fr) minmax(60px,100px) 40px;gap:10px;align-items:center;min-height:28px;padding:2px 0;')}>
                  <span style={sx('font-size:16px;font-weight:600;color:#22272E;min-width:0;line-height:1.3;text-wrap:pretty;')}>
                    {g.name} <span style={sx('font-size:14px;font-weight:700;color:#54606C;')}>{g.lvl}</span>
                  </span>
                  <span style={sx('position:relative;height:8px;border-radius:4px;background:rgba(34,39,46,.08);')}>
                    <span style={sx('position:absolute;left:0;top:0;bottom:0;border-radius:4px;transition:width 1.2s cubic-bezier(.22,.61,.36,1);width:' + g.cur + '%;background:' + (g.hot ? '#B3261E' : '#2F5C97') + ';')} />
                    <span style={sx('position:absolute;top:-3px;bottom:-3px;width:2px;background:#22272E;left:' + g.trip + ';')} />
                  </span>
                  <span style={sx('font-size:16px;font-weight:800;font-variant-numeric:tabular-nums;text-align:right;color:' + (g.hot ? '#B3261E' : '#22272E') + ';')}>{g.pct}</span>
                </span>
              ))}
            </section>
            <section aria-label="Decision path" style={sx(BLOCK)}>
              <span style={sx(HEAD)}>
                <span style={sx(H14)}>Decision path · {range}</span>
              </span>
              {mon.data && layers.length === 0 && <span style={sx('font-size:16px;color:#54606C;padding:4px 0;')}>No decision path for this range.</span>}
              {layers.map((p) => (
                <span key={p.key} style={sx('display:grid;grid-template-columns:minmax(0,1fr) 110px 52px 56px;gap:10px;align-items:center;min-height:26px;')}>
                  <span style={sx('font-size:16px;font-weight:600;color:#22272E;min-width:0;')}>{p.name}</span>
                  <span style={sx('height:6px;border-radius:3px;background:rgba(34,39,46,.08);overflow:hidden;')}>
                    {p.passPct !== null && <span style={sx('display:block;height:100%;border-radius:3px;transition:width 1s;width:' + p.passPct.toFixed(1) + '%;background:' + (p.passPct > 95 ? '#1F6B52' : '#B3261E') + ';')} />}
                  </span>
                  <span style={sx('font-size:16px;font-weight:700;font-variant-numeric:tabular-nums;color:#22272E;text-align:right;')}>{p.passPct === null ? '–' : p.passPct.toFixed(1) + '%'}</span>
                  <span title={p.reason ?? undefined} style={sx('font-size:14px;font-variant-numeric:tabular-nums;color:#54606C;text-align:right;')}>{p.ms === null ? '–' : Math.round(p.ms) + 'ms'}</span>
                </span>
              ))}
            </section>
            <section aria-label="Verdicts" style={sx(BLOCK)}>
              <span style={sx(HEAD)}>
                <span style={sx(H14)}>Verdicts · {range}</span>
                <span style={sx('font-size:14px;color:#54606C;white-space:nowrap;')}>{vm ? vm.total + ' decisions' : '–'}</span>
              </span>
              <span style={sx('display:flex;height:12px;border-radius:6px;overflow:hidden;gap:2px;')}>
                {vb.map((x) => (
                  <span key={x[0]} style={sx('display:block;height:100%;transition:flex-grow 1s cubic-bezier(.22,.61,.36,1);flex:' + x[1] + ' 1 0;background:' + x[2] + ';')} />
                ))}
              </span>
              <span style={sx('display:flex;gap:6px 16px;flex-wrap:wrap;')}>
                {vb.map((x) => (
                  <span key={x[0]} style={sx('display:inline-flex;align-items:baseline;gap:5px;font-size:16px;')}>
                    <span style={sx('width:8px;height:8px;border-radius:2px;background:' + x[2] + ';')} />
                    <b style={sx('color:#22272E;font-variant-numeric:tabular-nums;')}>{vSum ? Math.round((x[1] / vSum) * 100) + '%' : '0%'}</b>
                    <span style={sx('color:#54606C;')}>{x[0]}</span>
                  </span>
                ))}
              </span>
            </section>
          </div>

          {/* Column 3: System Pulse or your building, human oversight, reviewers, the thread */}
          <div style={sx('display:flex;flex-direction:column;min-width:0;')}>
            {op && (
              <>
                <Link to="/pulse" style={sx('display:flex;align-items:center;gap:8px;min-height:34px;margin-bottom:8px;padding:0 10px;border-radius:8px;background:' + pulseBg + ';text-decoration:none;min-width:0;')}>
                  <span style={sx('width:8px;height:8px;border-radius:50%;flex:0 0 8px;background:' + pulseC + ';')} />
                  <span style={sx('font-size:16px;font-weight:700;color:#22272E;white-space:nowrap;')}>System Pulse</span>
                  <span style={sx('flex:1 1 auto;min-width:0;font-size:14px;color:#3C454F;overflow-wrap:break-word;')}>{pb ? (pb.label ?? '') + ' · ' + pb.aria : ''}</span>
                  <span style={sx('font-size:16px;font-weight:700;color:#2F5C97;')}>→</span>
                </Link>
                {bldAlerts.length > 0 && (
                  <div style={sx('display:flex;flex-direction:column;gap:3px;min-width:0;margin-bottom:12px;')}>
                    <span style={sx(HEAD)}>
                      <span style={sx(H14)}>From buildings</span>
                      <span style={sx('font-size:14px;color:#54606C;white-space:nowrap;')}>{bldAlerts.length > 1 ? bldAlerts.length + ' today · the rest under the bell' : '1 today'}</span>
                    </span>
                    {bldAlerts.slice(0, 1).map((a) => (
                      <span key={a.id} style={sx('display:flex;align-items:center;gap:8px;min-height:32px;border-top:1px solid rgba(34,39,46,.07);min-width:0;animation:riseIn .3s cubic-bezier(.22,.61,.36,1);')}>
                        <span style={sx('width:8px;height:8px;border-radius:50%;flex:0 0 8px;background:' + (a.severity === 'CRITICAL' ? '#B3261E' : '#2F5C97') + ';')} />
                        <span style={sx('display:flex;flex-direction:column;min-width:0;flex:1 1 auto;')}>
                          <span style={sx('font-size:16px;font-weight:700;color:#22272E;line-height:1.3;')}>{a.headline ?? a.title}</span>
                          <span style={sx('font-size:14px;color:#54606C;')}>{[a.subjectName, a.createdAt ? new Date(a.createdAt).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }) : null].filter(Boolean).join(' · ')}</span>
                        </span>
                      </span>
                    ))}
                  </div>
                )}
              </>
            )}
            {fa && (
              <div style={sx('display:flex;flex-direction:column;gap:3px;min-width:0;margin-bottom:12px;')}>
                <span style={sx(HEAD)}>
                  <span style={sx(H14)}>Your building</span>
                  <span style={sx('font-size:14px;color:#54606C;white-space:nowrap;')}>{bldName} · checked every 60s</span>
                </span>
                {building.data && bld.length === 0 && <span style={sx('font-size:14px;color:#54606C;')}>Your building is not reporting yet.</span>}
                {bld.map((c) => (
                  <span key={c.name} style={sx('display:flex;align-items:center;gap:8px;min-height:32px;border-top:1px solid rgba(34,39,46,.07);min-width:0;')}>
                    <span style={sx('width:8px;height:8px;border-radius:50%;flex:0 0 8px;background:' + c.c + ';')} />
                    <span style={sx('display:flex;flex-direction:column;min-width:0;flex:1 1 auto;')}>
                      <span style={sx('font-size:16px;font-weight:600;color:#22272E;line-height:1.3;')}>{c.name}</span>
                      <span style={sx('font-size:14px;color:#54606C;line-height:1.35;text-wrap:pretty;')}>{c.meta}</span>
                    </span>
                    <span style={sx('font-size:14px;font-weight:800;letter-spacing:.06em;color:' + c.c + ';white-space:nowrap;')}>{c.state}</span>
                  </span>
                ))}
                <span style={sx('display:flex;align-items:center;gap:8px 12px;flex-wrap:wrap;padding-top:8px;border-top:1px solid rgba(34,39,46,.07);min-width:0;')}>
                  <Act
                    disabled={!!roReason || !pause.data}
                    title={roReason ?? undefined}
                    onClick={() => setPending({ kind: 'pause', paused: !bPaused })}
                    css={'display:inline-flex;align-items:center;min-height:34px;padding:0 12px;border-radius:8px;font-size:16px;font-weight:700;text-decoration:none;white-space:nowrap;' + (bPaused ? 'background:#B3261E;color:#fff;' : 'background:#fff;color:#B3261E;border:1px solid rgba(179,38,30,.4);')}
                  >
                    {bPaused ? 'Resume AI actions' : 'Pause AI actions in my building'}
                  </Act>
                  <span style={sx('flex:1 1 160px;min-width:0;font-size:14px;color:#54606C;line-height:1.4;text-wrap:pretty;')}>
                    {bPaused ? 'Paused. Every AI action here is held for a person. Atlas keeps talking. Aithora has been alerted.' : 'Holds every AI action for a person. Atlas keeps talking. Aithora is alerted.'}
                  </span>
                </span>
              </div>
            )}
            <section aria-label="Human oversight" style={sx(BLOCK)}>
              <span style={sx(HEAD)}>
                <span style={sx(H14)}>Human oversight</span>
                <span style={sx('font-size:14px;color:#54606C;white-space:nowrap;')}>{(fa ? bldName : 'all buildings') + ' · last 24h'}</span>
              </span>
              <span style={sx('display:flex;gap:6px 18px;flex-wrap:wrap;padding:4px 0 6px;min-width:0;')}>
                {hoStats.map((k) => (
                  <span key={k.k} title={k.tip} style={sx('display:inline-flex;flex-direction:column;min-width:0;')}>
                    <span style={sx('font-size:17px;font-weight:800;letter-spacing:-.01em;font-variant-numeric:tabular-nums;color:#22272E;')}>{k.v}</span>
                    <span style={sx('font-size:14px;color:#54606C;white-space:nowrap;')}>{k.k}</span>
                  </span>
                ))}
              </span>
              {hu && !hu.sufficient && <span style={sx('font-size:14px;color:#54606C;')}>{'Fewer than ' + hu.floor + ' decisions in the window, so no rates yet.'}</span>}
              <span style={sx('display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:0 16px;min-width:0;')}>
                {hoGates.map((g) => (
                  <span key={g.name} title={g.tip} style={sx('display:grid;grid-template-columns:minmax(0,1fr) 36px 32px;gap:6px;align-items:center;min-height:28px;border-top:1px solid rgba(34,39,46,.07);min-width:0;')}>
                    <span style={sx('font-size:16px;font-weight:600;color:#22272E;min-width:0;overflow-wrap:break-word;')}>{g.name}</span>
                    <span style={sx('display:block;height:6px;border-radius:3px;background:rgba(34,39,46,.08);overflow:hidden;')}>
                      <span style={sx(g.bar)} />
                    </span>
                    <span style={sx('font-size:14px;font-weight:800;font-variant-numeric:tabular-nums;text-align:right;color:' + g.c + ';')}>{g.pct}</span>
                  </span>
                ))}
              </span>
            </section>
            {!short && (
              <section aria-label="Who is reviewing" style={sx(BLOCK)}>
                <span style={sx(HEAD)}>
                  <span style={sx(H14)}>Who is reviewing</span>
                  <span style={sx('font-size:14px;color:#54606C;white-space:nowrap;')}>decided today · typical time</span>
                </span>
                {reviewers.data && reviewers.data.reviewers.length === 0 && <span style={sx('font-size:14px;color:#54606C;')}>Nobody has decided a held item in the last 24h.</span>}
                {(reviewers.data?.reviewers ?? []).map((r) => (
                  <span key={r.userId ?? r.name ?? ''} style={sx('display:grid;grid-template-columns:28px minmax(0,1fr) auto auto;gap:8px;align-items:center;min-height:32px;border-top:1px solid rgba(34,39,46,.07);min-width:0;')}>
                    <span style={sx('width:26px;height:26px;border-radius:50%;background:rgba(47,92,151,.12);color:#264C7E;font-size:14px;font-weight:800;display:flex;align-items:center;justify-content:center;')}>{r.initials ?? initialsOf(r.name)}</span>
                    <span style={sx('display:flex;flex-direction:column;min-width:0;')}>
                      <span style={sx('font-size:16px;font-weight:700;color:#22272E;overflow-wrap:break-word;')}>{r.name ?? 'Unnamed reviewer'}</span>
                      <span style={sx('font-size:14px;color:#54606C;overflow-wrap:break-word;')}>{r.note ?? r.role ?? ''}</span>
                    </span>
                    <span style={sx('font-size:14px;color:#54606C;font-variant-numeric:tabular-nums;white-space:nowrap;')}>{durText(r.medianTimeSec)}</span>
                    <span style={sx('font-size:16px;font-weight:800;font-variant-numeric:tabular-nums;color:' + (r.stillOpen ? '#8A5A00' : '#22272E') + ';white-space:nowrap;text-align:right;min-width:52px;')}>
                      {r.decided + ' decided' + (r.stillOpen ? ' · ' + r.stillOpen + ' open' : '')}
                    </span>
                  </span>
                ))}
              </section>
            )}
            <section aria-label="Escalation thread" style={sx(BLOCK)}>
              <span style={sx(HEAD)}>
                <span style={sx(H14)}>Escalation thread</span>
                <span style={sx('font-size:14px;color:#54606C;flex:1 1 100%;line-height:1.35;margin-bottom:4px;')}>
                  {short && steps.length ? 'step ' + (nowIdx + 1) + ' of ' + steps.length + ' · one signal becoming a clinical action' : 'one signal becoming a clinical action'}
                </span>
              </span>
              {thread.data && steps.length === 0 && <span style={sx('font-size:14px;color:#54606C;')}>No escalation is in progress.</span>}
              {shownSteps.map((s, i) => {
                const on = s.state !== 'waiting';
                const cur = s.state === 'now';
                return (
                  <span key={i} style={sx('display:flex;align-items:center;gap:10px;min-height:27px;padding-left:4px;border-left:2px solid ' + (on ? '#2F5C97' : 'rgba(34,39,46,.14)') + ';' + (on ? '' : 'opacity:.55;') + (cur ? 'animation:riseIn .35s cubic-bezier(.22,.61,.36,1);' : ''))}>
                    <span style={sx('width:9px;height:9px;border-radius:50%;flex:0 0 9px;margin-left:-10px;background:' + (on ? '#2F5C97' : '#FCFAF4') + ';border:2px solid ' + (on ? '#2F5C97' : 'rgba(34,39,46,.25)') + ';' + (cur ? 'animation:breathe 1.2s ease-in-out infinite;' : ''))} />
                    <span style={sx('display:flex;flex-direction:column;min-width:0;flex:1 1 auto;')}>
                      <span style={sx('font-size:16px;font-weight:700;color:#22272E;')}>{s.what}</span>
                      <span style={sx('font-size:14px;color:#54606C;font-variant-numeric:tabular-nums;')}>{[s.meta, s.ledgerRef].filter(Boolean).join(' · ')}</span>
                    </span>
                    <span style={sx('font-size:14px;font-weight:700;color:' + (on ? (cur ? '#2F5C97' : '#1F6B52') : '#54606C') + ';white-space:nowrap;')}>{on ? (cur ? 'NOW' : '✓ sealed') : 'waiting'}</span>
                  </span>
                );
              })}
            </section>
          </div>
        </div>
      </div>

      {dlg && <ConfirmDialog title={dlg.title} body={dlg.body} confirmLabel={dlg.label} danger={dlg.danger} reason={dlg.reason} onConfirm={dlg.run} onClose={() => setPending(null)} />}
    </div>
  );
}

function initialsOf(name: string | null): string {
  if (!name) return '';
  return name
    .replace(/[^A-Za-z. ]/g, '')
    .split(/[ .]+/)
    .filter(Boolean)
    .map((w) => w[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();
}
